import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, CloudRain, Zap, CheckCircle, ArrowRight, ChevronDown, BarChart2, DollarSign, Clock } from 'lucide-react';
import Impressum from './Impressum';

const Versicherungen = () => {
  const [scrolled, setScrolled] = useState(false);
  const [currentLang, setCurrentLang] = useState('de');
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [showImpressum, setShowImpressum] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const translations = {
    de: {
      nav: {
        home: "Startseite",
        demo: "Demo anfordern"
      },
      hero: {
        title: "Parametrische Versicherungen",
        subtitle: "Automatisierter Schutz gegen Klimarisiken mit sofortiger Auszahlung",
        description: "Smart Contract-basierte Mikroversicherungen die bei definierten Wetterereignissen automatisch auslösen"
      },
      features: {
        title: "Warum parametrische Versicherungen?",
        items: [
          {
            title: "Sofortige Auszahlung",
            desc: "Automatische Zahlung innerhalb von 24 Stunden nach Ereigniseintritt"
          },
          {
            title: "Transparente Bedingungen",
            desc: "Klare, objektive Parameter - keine subjektiven Schadensbewertungen"
          },
          {
            title: "Niedrige Kosten",
            desc: "Keine Gutachter oder langwierige Schadensabwicklung nötig"
          },
          {
            title: "Blockchain-Sicherheit",
            desc: "Unveränderbare Smart Contracts garantieren faire Abwicklung"
          }
        ]
      },
      how: {
        title: "So funktioniert's",
        steps: [
          {
            title: "Parameter definieren",
            desc: "Gemeinsam legen wir objektive Auslöser fest (z.B. Niederschlag < 20mm)"
          },
          {
            title: "Smart Contract erstellen",
            desc: "Automatisierter Vertrag wird auf der Blockchain gespeichert"
          },
          {
            title: "Monitoring",
            desc: "Wetterdaten werden kontinuierlich von offiziellen Stationen überwacht"
          },
          {
            title: "Automatische Auszahlung",
            desc: "Bei Erreichen der Parameter erfolgt sofortige Kompensation"
          }
        ]
      },
      coverage: {
        title: "Versicherbare Risiken",
        items: [
          { name: "Dürre", desc: "Zu wenig Niederschlag in kritischen Wachstumsphasen" },
          { name: "Überschwemmung", desc: "Extremniederschläge und Hochwasser" },
          { name: "Frost", desc: "Spätfrost während der Blütezeit" },
          { name: "Hagel", desc: "Schwere Hagelschäden an Kulturen" },
          { name: "Sturm", desc: "Windgeschwindigkeiten über definierten Schwellenwerten" },
          { name: "Hitze", desc: "Extreme Temperaturen während sensibler Phasen" }
        ]
      },
      benefits: {
        title: "Ihre Vorteile",
        items: [
          "Planungssicherheit für Ihre Betriebsführung",
          "Schnelle Liquidität bei Ernteausfällen",
          "Faire, transparente Preisgestaltung",
          "Keine komplizierte Schadensabwicklung",
          "Flexibel an Ihre Bedürfnisse anpassbar"
        ]
      },
      cta: {
        title: "Schützen Sie Ihren Betrieb vor Klimarisiken",
        subtitle: "Lassen Sie uns gemeinsam eine maßgeschneiderte Lösung entwickeln",
        button: "Beratungsgespräch vereinbaren"
      }
    },
    en: {
      nav: {
        home: "Home",
        demo: "Request Demo"
      },
      hero: {
        title: "Parametric Insurance",
        subtitle: "Automated protection against climate risks with instant payout",
        description: "Smart contract-based microinsurance that triggers automatically on defined weather events"
      },
      features: {
        title: "Why parametric insurance?",
        items: [
          {
            title: "Instant Payout",
            desc: "Automatic payment within 24 hours of event occurrence"
          },
          {
            title: "Transparent Terms",
            desc: "Clear, objective parameters - no subjective damage assessments"
          },
          {
            title: "Low Costs",
            desc: "No assessors or lengthy claims processing needed"
          },
          {
            title: "Blockchain Security",
            desc: "Immutable smart contracts guarantee fair processing"
          }
        ]
      },
      how: {
        title: "How it works",
        steps: [
          {
            title: "Define Parameters",
            desc: "Together we set objective triggers (e.g., rainfall < 20mm)"
          },
          {
            title: "Create Smart Contract",
            desc: "Automated contract is stored on the blockchain"
          },
          {
            title: "Monitoring",
            desc: "Weather data is continuously monitored from official stations"
          },
          {
            title: "Automatic Payout",
            desc: "Immediate compensation when parameters are reached"
          }
        ]
      },
      coverage: {
        title: "Insurable Risks",
        items: [
          { name: "Drought", desc: "Insufficient rainfall during critical growth phases" },
          { name: "Flooding", desc: "Extreme rainfall and floods" },
          { name: "Frost", desc: "Late frost during flowering period" },
          { name: "Hail", desc: "Severe hail damage to crops" },
          { name: "Storm", desc: "Wind speeds above defined thresholds" },
          { name: "Heat", desc: "Extreme temperatures during sensitive phases" }
        ]
      },
      benefits: {
        title: "Your Benefits",
        items: [
          "Planning security for your operations",
          "Quick liquidity in case of crop failures",
          "Fair, transparent pricing",
          "No complicated claims processing",
          "Flexibly adaptable to your needs"
        ]
      },
      cta: {
        title: "Protect your farm from climate risks",
        subtitle: "Let's develop a tailored solution together",
        button: "Schedule consultation"
      }
    },
    it: {
      nav: {
        home: "Home",
        demo: "Richiedi Demo"
      },
      hero: {
        title: "Assicurazioni Parametriche",
        subtitle: "Protezione automatizzata contro i rischi climatici con pagamento istantaneo",
        description: "Microassicurazioni basate su smart contract che si attivano automaticamente su eventi meteorologici definiti"
      },
      features: {
        title: "Perché le assicurazioni parametriche?",
        items: [
          {
            title: "Pagamento Istantaneo",
            desc: "Pagamento automatico entro 24 ore dal verificarsi dell'evento"
          },
          {
            title: "Condizioni Trasparenti",
            desc: "Parametri chiari e oggettivi - nessuna valutazione soggettiva dei danni"
          },
          {
            title: "Costi Ridotti",
            desc: "Nessun perito o lunga elaborazione dei sinistri necessaria"
          },
          {
            title: "Sicurezza Blockchain",
            desc: "Smart contract immutabili garantiscono un'elaborazione equa"
          }
        ]
      },
      how: {
        title: "Come funziona",
        steps: [
          {
            title: "Definire i Parametri",
            desc: "Insieme stabiliamo trigger oggettivi (es. precipitazioni < 20mm)"
          },
          {
            title: "Creare Smart Contract",
            desc: "Il contratto automatizzato viene memorizzato sulla blockchain"
          },
          {
            title: "Monitoraggio",
            desc: "I dati meteo sono monitorati continuamente da stazioni ufficiali"
          },
          {
            title: "Pagamento Automatico",
            desc: "Compensazione immediata al raggiungimento dei parametri"
          }
        ]
      },
      coverage: {
        title: "Rischi Assicurabili",
        items: [
          { name: "Siccità", desc: "Precipitazioni insufficienti durante le fasi critiche di crescita" },
          { name: "Alluvioni", desc: "Precipitazioni estreme e inondazioni" },
          { name: "Gelo", desc: "Gelate tardive durante il periodo di fioritura" },
          { name: "Grandine", desc: "Gravi danni da grandine alle colture" },
          { name: "Tempesta", desc: "Velocità del vento sopra le soglie definite" },
          { name: "Calore", desc: "Temperature estreme durante le fasi sensibili" }
        ]
      },
      benefits: {
        title: "I Tuoi Vantaggi",
        items: [
          "Sicurezza di pianificazione per le tue operazioni",
          "Liquidità rapida in caso di perdite di raccolto",
          "Prezzi equi e trasparenti",
          "Nessuna complicata elaborazione dei sinistri",
          "Flessibilmente adattabile alle tue esigenze"
        ]
      },
      cta: {
        title: "Proteggi la tua azienda dai rischi climatici",
        subtitle: "Sviluppiamo insieme una soluzione su misura",
        button: "Prenota una consulenza"
      }
    },
    es: {
      nav: {
        home: "Inicio",
        demo: "Solicitar Demo"
      },
      hero: {
        title: "Seguros Paramétricos",
        subtitle: "Protección automatizada contra riesgos climáticos con pago instantáneo",
        description: "Microseguros basados en contratos inteligentes que se activan automáticamente en eventos climáticos definidos"
      },
      features: {
        title: "¿Por qué seguros paramétricos?",
        items: [
          {
            title: "Pago Instantáneo",
            desc: "Pago automático dentro de 24 horas de ocurrencia del evento"
          },
          {
            title: "Términos Transparentes",
            desc: "Parámetros claros y objetivos - sin evaluaciones subjetivas de daños"
          },
          {
            title: "Costos Bajos",
            desc: "Sin peritos ni procesamiento largo de reclamos"
          },
          {
            title: "Seguridad Blockchain",
            desc: "Contratos inteligentes inmutables garantizan procesamiento justo"
          }
        ]
      },
      how: {
        title: "Cómo funciona",
        steps: [
          {
            title: "Definir Parámetros",
            desc: "Juntos establecemos disparadores objetivos (ej. lluvia < 20mm)"
          },
          {
            title: "Crear Contrato Inteligente",
            desc: "El contrato automatizado se almacena en blockchain"
          },
          {
            title: "Monitoreo",
            desc: "Los datos climáticos se monitorean continuamente desde estaciones oficiales"
          },
          {
            title: "Pago Automático",
            desc: "Compensación inmediata cuando se alcanzan los parámetros"
          }
        ]
      },
      coverage: {
        title: "Riesgos Asegurables",
        items: [
          { name: "Sequía", desc: "Precipitaciones insuficientes durante fases críticas de crecimiento" },
          { name: "Inundaciones", desc: "Precipitaciones extremas e inundaciones" },
          { name: "Heladas", desc: "Heladas tardías durante el período de floración" },
          { name: "Granizo", desc: "Daños severos por granizo a los cultivos" },
          { name: "Tormenta", desc: "Velocidades de viento por encima de umbrales definidos" },
          { name: "Calor", desc: "Temperaturas extremas durante fases sensibles" }
        ]
      },
      benefits: {
        title: "Tus Beneficios",
        items: [
          "Seguridad de planificación para tus operaciones",
          "Liquidez rápida en caso de pérdidas de cosecha",
          "Precios justos y transparentes",
          "Sin procesamiento complicado de reclamos",
          "Flexiblemente adaptable a tus necesidades"
        ]
      },
      cta: {
        title: "Protege tu granja de los riesgos climáticos",
        subtitle: "Desarrollemos juntos una solución a medida",
        button: "Programar consulta"
      }
    }
  };

  const t = translations[currentLang];

  const languages = [
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'es', name: 'Español', flag: '🇪🇸' }
  ];

  const ZebraLogo = () => (
    <img 
      src="/logo.png" 
      alt="Lazzaro Logo" 
      className="w-full h-full object-contain"
    />
  );

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-gray-900/95 backdrop-blur-xl shadow-2xl' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-12 h-12 relative">
                <ZebraLogo />
              </div>
              <div>
                <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-amber-500 bg-clip-text text-transparent">Lazzaro1</span>
                <p className="text-xs text-amber-400/80 font-medium">SMART DATA FOR SMARTER FARMING</p>
              </div>
            </Link>
            
            <div className="flex items-center space-x-8">
              <Link to="/" className="hover:text-cyan-400 transition-colors">{t.nav.home}</Link>
              
              {/* Language Selector */}
              <div className="relative">
                <button
                  onClick={() => setLangMenuOpen(!langMenuOpen)}
                  className="flex items-center space-x-2 hover:text-cyan-400 transition-colors"
                >
                  <span>{languages.find(l => l.code === currentLang)?.flag}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {langMenuOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-gray-900 border border-gray-700 rounded-lg shadow-xl">
                    {languages.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setCurrentLang(lang.code);
                          setLangMenuOpen(false);
                        }}
                        className={`w-full px-4 py-2 text-left hover:bg-gray-800 transition-colors flex items-center space-x-2 ${
                          currentLang === lang.code ? 'text-cyan-400' : ''
                        }`}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <button className="bg-gradient-to-r from-cyan-500 to-amber-600 px-6 py-2 rounded-full hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 transform hover:-translate-y-0.5">
                {t.nav.demo}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-transparent to-indigo-900/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white mb-6">
              <Shield className="w-16 h-16" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              {t.hero.title}
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-4 max-w-3xl mx-auto">
              {t.hero.subtitle}
            </p>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              {t.hero.description}
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">{t.features.title}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.features.items.map((feature, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-gray-800/50 hover:bg-gray-800 transition-all duration-300">
                <div className="inline-flex p-3 rounded-lg bg-gradient-to-br from-blue-500/20 to-indigo-600/20 text-blue-400 mb-4">
                  {index === 0 && <Clock className="w-8 h-8" />}
                  {index === 1 && <BarChart2 className="w-8 h-8" />}
                  {index === 2 && <DollarSign className="w-8 h-8" />}
                  {index === 3 && <Shield className="w-8 h-8" />}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">{t.how.title}</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {t.how.steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-2xl font-bold mb-4 mx-auto">
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-gray-400">{step.desc}</p>
                </div>
                {index < t.how.steps.length - 1 && (
                  <ArrowRight className="hidden md:block absolute top-8 -right-4 w-8 h-8 text-gray-600" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">{t.coverage.title}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.coverage.items.map((risk, index) => (
              <div key={index} className="p-6 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 hover:border-blue-500/50 transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <CloudRain className="w-8 h-8 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{risk.name}</h3>
                    <p className="text-gray-400">{risk.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">{t.benefits.title}</h2>
          <div className="max-w-3xl mx-auto">
            <div className="space-y-4">
              {t.benefits.items.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-3 p-4 rounded-lg bg-gray-900/50">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <p className="text-lg">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 to-indigo-900/20"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t.cta.title}
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            {t.cta.subtitle}
          </p>
          <button 
            onClick={() => window.location.href = 'mailto:office@lazzaro.one?subject=Beratungsgespräch%20Parametrische%20Versicherungen'}
            className="group bg-gradient-to-r from-cyan-500 to-blue-600 px-8 py-4 rounded-full text-lg font-semibold hover:shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300 transform hover:-translate-y-1 flex items-center justify-center mx-auto"
          >
            {t.cta.button}
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-12 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Link to="/" className="inline-flex items-center space-x-3 mb-4">
              <div className="w-10 h-10">
                <ZebraLogo />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-amber-500 bg-clip-text text-transparent">Lazzaro1</span>
            </Link>
            <p className="text-gray-400">© 2024 Lazzaro One. Alle Rechte vorbehalten.</p>
            
            {/* Add Impressum Link */}
            <div className="mt-4">
              <button
                onClick={() => setShowImpressum(true)}
                className="text-gray-400 hover:text-cyan-400 transition-colors text-sm"
              >
                Impressum
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Add Impressum Modal */}
      <Impressum isOpen={showImpressum} onClose={() => setShowImpressum(false)} />
    </div>
  );
};

export default Versicherungen;